/**
 * Where a node created from a preset appears: where the card was dropped (in whatever view
 * the graph window shows: the spot it answers, `shared/spot.ts`), next to the selected node,
 * or near the middle of the view. And the drag of a card: onto the graph (adds the node) or
 * onto a folder of the browser's tree (moves or copies the preset there).
 */
import type { Preset } from "@protocol/Preset";
import { toast } from "../../shared/toast";
import { spotAt, type Spot } from "../../shared/spot";
import type { Presets } from "../../state/presets";
import type { Scene } from "../../state/scene";
import type { View } from "../../state/view";

/** A point in world coordinates (where nodes live). */
type WorldPoint = { x: number; y: number; z?: number };

/** What adding a preset needs. */
export interface PlaceDeps {
  presets: Presets;
  view: View;
  scene: Scene;
}

/** Near the middle of the view, a little scattered so repeated clicks don't stack. */
function spawnPoint(scene: Scene): WorldPoint {
  return scene.toWorld(scene.width / 2 + (Math.random() - 0.5) * 80, scene.height / 2 + (Math.random() - 0.5) * 80);
}

/**
 * Adds the preset's node at `spot` (or beside the selected node, or near the middle of the
 * view), makes sure its categories and link kinds are shown, and selects it.
 */
export async function addPreset({ presets, view, scene }: PlaceDeps, preset: Preset, spot?: Spot): Promise<void> {
  const s = view.state.selection;
  const near = s?.type === "node" ? scene.byId.get(s.id) : undefined;
  const point: WorldPoint | null = spot ? spot.hint : near?.visible ? { x: near.x + 60 + Math.random() * 60, y: near.y + (Math.random() - 0.5) * 140, z: near.z } : spawnPoint(scene);
  for (const c of preset.categories.length ? preset.categories : [""]) view.showCategory(c);
  for (const l of preset.links) view.showLinkKind(l.kind);
  const id = await presets.apply(preset, (name) => point && scene.hint(name, point.x, point.y, point.z), spot);
  if (id) view.selectNode(id);
}

/** What a card drag reports. */
export interface DragHooks {
  /** Where the ghost card is (screen point), or `null` when the drag ends. */
  ghost(state: { preset: Preset; x: number; y: number } | null): void;
  /** The card was dropped on the graph, at this spot. */
  drop(spot: Spot): void;
  /** The card was dropped on a folder of the tree (`data-drop-source`, `data-drop-folder`). */
  toFolder(source: string, folder: string): void;
}

/** How far (in pixels) the pointer must move before a press becomes a drag. */
const DRAG_THRESHOLD = 6;

/**
 * Follows a press on a card (mouse and pen; touch uses tap to add). Dropping it on a folder
 * of the tree moves or copies it there; anywhere on the graph window, overlays included, adds
 * the node there.
 */
export function dragPreset(e: PointerEvent, preset: Preset, hooks: DragHooks): void {
  if (e.pointerType === "touch" || e.button !== 0) return;
  const [x0, y0] = [e.clientX, e.clientY];
  let moved = false;
  /** Shows the ghost once the press moved far enough. */
  const move = (ev: PointerEvent) => {
    if (!moved && Math.hypot(ev.clientX - x0, ev.clientY - y0) > DRAG_THRESHOLD) moved = true;
    if (moved) hooks.ghost({ preset, x: ev.clientX, y: ev.clientY });
  };
  /** Ends the drag: adds the node if the card was dropped on the graph. */
  const up = (ev: PointerEvent) => {
    removeEventListener("pointermove", move);
    removeEventListener("pointerup", up);
    hooks.ghost(null);
    if (!moved) return;
    // Swallow the click that follows the drag.
    addEventListener("click", (c) => c.stopPropagation(), { capture: true, once: true });
    const under = document.elementFromPoint(ev.clientX, ev.clientY);
    const folder = under?.closest<HTMLElement>("[data-drop-source]");
    if (folder) return hooks.toFolder(folder.dataset.dropSource ?? "", folder.dataset.dropFolder ?? "");
    const spot = spotAt(ev.clientX, ev.clientY);
    if (!spot) return void toast("Drop the preset on the graph to add it.");
    hooks.drop(spot);
  };
  addEventListener("pointermove", move);
  addEventListener("pointerup", up);
}
